/**
 * Converts degrees to radians
 * @param degreeValue - Angle in degrees
 * @returns Angle in radians
 */
const getRadianAngle = (degreeValue: number) => {
  return (degreeValue * Math.PI) / 180;
};

/**
 * Creates an HTMLImageElement from a URL with CORS support
 * @param url - Image source URL
 * @returns Promise that resolves to a loaded HTMLImageElement
 */
const createImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
    return image;
  });
};

type ImageCropType = {
  unit?: 'px' | '%';
  x: number;
  y: number;
  width: number;
  height: number;
};

/**
 * Crops an image based on pixel coordinates and returns a new File.
 *
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 *
 * The function:
 * 1. Loads the image from the source URL
 * 2. Creates a canvas and draws the cropped portion
 * 3. Converts the canvas to a Blob and then to a File
 * 4. Includes a polyfill for Edge browsers that don't support toBlob
 *
 * @param imageSrc - Source URL of the image to crop
 * @param pixelCrop - Crop area in pixels (x, y, width, height)
 * @param originalFile - Original File object (used for filename and type)
 * @returns Promise that resolves to a new cropped File
 */
const cropImage = async (
  imageSrc: string,
  pixelCrop: ImageCropType,
  originalFile: File,
): Promise<File> => {
  // Create image object
  const image = await createImage(imageSrc);

  // Create canvas object to draw image on
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // set canvas width to final desired crop size - this will clear existing context
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // draw rotated image and store data.
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );

  /**
   * Add polyfill for Microsoft Edge browsers (legacy versions).
   * Retrieved from:
   * https://stackoverflow.com/questions/47475647/canvas-to-blob-on-edge
   * with additional provision from revision:
   * https://wiki.developer.mozilla.org/en-US/docs/Web/API/HTMLCanvasElement/toBlob$revision/1559998
   *
   * Note: Modern Edge (Chromium-based) supports toBlob natively, but this
   * polyfill ensures compatibility with older versions.
   */
  if (!HTMLCanvasElement.prototype.toBlob) {
    Object.defineProperty(HTMLCanvasElement.prototype, 'toBlob', {
      configurable: true,
      value: function (callback: (arg0: Blob) => void, type: any, quality: any) {
        var dataURL = this.toDataURL(type, quality).split(',')[1];
        setTimeout(function () {
          var binStr = atob(dataURL),
            len = binStr.length,
            arr = new Uint8Array(len);

          for (var i = 0; i < len; i++) {
            arr[i] = binStr.charCodeAt(i);
          }

          callback(new Blob([arr], { type: type || 'image/jpeg' }));
        });
      },
    });
  }

  // As a blob
  return new Promise(function (resolve) {
    canvas.toBlob(
      function (blob) {
        const fileBlob = new File([blob], originalFile.name, { type: blob.type });
        resolve(fileBlob);
      },
      'image/jpeg',
      1,
    );
  });
};

export { getRadianAngle, createImage, cropImage };
